import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  TextField,
  Stack,
  MenuItem,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { Edit, Delete, Add, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { couponBatchService } from '../services/couponBatchService';
import { campaignService } from '../services/campaignService';
import { storeService } from '../services/storeService';
import { CouponBatch, Campaign, Store } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from '../components/AppHeader';

  const CouponBatchList: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [couponBatches, setCouponBatches] = useState<CouponBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [filters, setFilters] = useState({
    campaignId: '',
    storeId: '',
    startNumber: '',
    endNumber: ''
  });

  const fetchCouponBatches = useCallback(async () => {
    try {
      setLoading(true);
      
      const requestFilters: any = { ...filters, page: page + 1, limit: rowsPerPage };
      
      Object.keys(requestFilters).forEach(key => {
        if (!requestFilters[key] || requestFilters[key] === '') {
          delete requestFilters[key];
        }
      });

      const data = await couponBatchService.getAllCouponBatches(requestFilters);
      
      setCouponBatches(data.couponBatches || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('❌ Erro ao carregar lotes de cupons:', error);
      setCouponBatches([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data || []);
    } catch (error) {
      console.error('❌ Erro ao carregar campanhas:', error);
      setCampaigns([]);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const data = await storeService.getStoresByAssociation();
      setStores(data.stores || []);
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error);
      setStores([]);
    }
  }, []);

  // Carregar campanhas e lojas apenas uma vez
  useEffect(() => {
    fetchCampaigns();
    fetchStores();
  }, []);

  // Carregar lotes quando filtros ou paginação mudam
  useEffect(() => {
    fetchCouponBatches();
  }, [filters, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este lote de cupons?')) {
      try {
        await couponBatchService.deleteCouponBatch(id);
        await fetchCouponBatches();
      } catch (error) {
        console.error('Erro ao excluir lote de cupons:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <AppHeader 
        title="Lotes de Cupons"
        subtitle="Gerencie lotes de cupons para suas campanhas"
        icon={<Add />}
        backTo="/dashboard"
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Lotes de Cupons
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/coupon-batches/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Novo Lote
          </Button>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtros de Pesquisa
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Campanha</InputLabel>
              <Select
                name="campaignId"
                value={filters.campaignId}
                onChange={handleFilterChange}
                label="Campanha"
              >
                <MenuItem value="">Todas</MenuItem>
                {campaigns?.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Loja</InputLabel>
              <Select
                name="storeId"
                value={filters.storeId}
                onChange={handleFilterChange}
                label="Loja"
              >
                <MenuItem value="">Todas</MenuItem>
                {stores?.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Número Inicial"
              name="startNumber"
              type="number"
              value={filters.startNumber}
              onChange={handleFilterChange}
              size="small"
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              label="Número Final"
              name="endNumber"
              type="number"
              value={filters.endNumber}
              onChange={handleFilterChange}
              size="small"
              inputProps={{ min: 1 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  campaignId: '',
                  storeId: '',
                  startNumber: '',
                  endNumber: ''
                });
                setPage(0);
              }}
            >
              Limpar
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campanha</TableCell>
                <TableCell>Loja</TableCell>
                <TableCell>Número Inicial</TableCell>
                <TableCell>Número Final</TableCell>
                <TableCell>Quantidade</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : couponBatches?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhum lote de cupons encontrado
                  </TableCell>
                </TableRow>
              ) : (
                couponBatches?.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      {batch.campaign?.name || `ID: ${batch.campaignId}`}
                    </TableCell>
                    <TableCell>
                      {batch.store?.name || `ID: ${batch.storeId}`}
                    </TableCell>
                    <TableCell>{batch.startNumber}</TableCell>
                    <TableCell>{batch.endNumber}</TableCell>
                    <TableCell>{batch.endNumber - batch.startNumber + 1}</TableCell>
                    <TableCell>{formatDate(batch.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/coupon-batches/${batch.id}/edit`)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(batch.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Container>
    </>
  );
}; 
export default CouponBatchList;